import * as React from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  Dialog,
  FormField,
  Input,
  Select,
  Skeleton,
  Textarea,
  useToast,
} from "@chotca/ui";
import { Pencil, Plus, Trash2 } from "lucide-react";
import {
  useAdminSystemPostingTemplates,
  useCreateAdminSystemPostingTemplate,
  useDeleteAdminSystemPostingTemplate,
  useUpdateAdminSystemPostingTemplate,
} from "@chotca/api-client";
import {
  ALL_SKILLS,
  BENEFITS_CATALOG,
  BENEFIT_LABEL_VI,
  EMPLOYMENT_TYPES,
  EMPLOYMENT_TYPE_LABEL_VI,
  SALARY_TYPES,
  SALARY_TYPE_LABEL_VI,
  SKILL_LABEL_VI,
  VENUE_TYPES,
  VENUE_TYPE_LABEL_VI,
  WORKER_POSITIONS,
  WORKER_POSITION_LABEL_VI,
  suggestSkillsForPosition,
  type AdminSystemPostingTemplateUpsertRequest,
  type EmploymentType,
  type Gender,
  type PostingTemplate,
  type SalaryType,
  type VenueType,
  type WorkerPosition,
} from "@chotca/config";

// ── Form state ─────────────────────────────────────────────────────────

type FormState = {
  code: string;
  label: string;
  name: string;
  position: WorkerPosition;
  venueType: VenueType;
  employmentType: EmploymentType;
  title: string;
  description: string;
  salaryMin: string;
  salaryMax: string;
  salaryType: SalaryType;
  defaultHeadcount: string;
  benefits: string[];
  skillsRequired: string[];
  skillsPreferred: string[];
  reqGender: "" | "MALE" | "FEMALE";
  reqMinAge: string;
  reqMaxAge: string;
  reqMinHeightCm: string;
  reqMotorbike: boolean;
  reqExcludeTattoo: boolean;
  reqKyc: boolean;
  softNotes: string;
};

const EMPTY_FORM: FormState = {
  code: "",
  label: "",
  name: "",
  position: "WAITER",
  venueType: "RESTAURANT",
  employmentType: "FULL_TIME",
  title: "",
  description: "",
  salaryMin: "0",
  salaryMax: "0",
  salaryType: "PER_MONTH",
  defaultHeadcount: "1",
  benefits: [],
  skillsRequired: [],
  skillsPreferred: [],
  reqGender: "",
  reqMinAge: "",
  reqMaxAge: "",
  reqMinHeightCm: "",
  reqMotorbike: false,
  reqExcludeTattoo: false,
  reqKyc: false,
  softNotes: "",
};

function templateToForm(t: PostingTemplate): FormState {
  return {
    code: t.code ?? "",
    label: t.label ?? t.name,
    name: t.name,
    position: t.position,
    venueType: t.venueType,
    employmentType: t.employmentType,
    title: t.title,
    description: t.description,
    salaryMin: String(t.salaryMin),
    salaryMax: String(t.salaryMax),
    salaryType: t.salaryType,
    defaultHeadcount: String(t.defaultHeadcount),
    benefits: [...t.benefits],
    skillsRequired: [...t.skillsRequired],
    skillsPreferred: [...t.skillsPreferred],
    reqGender:
      t.requirements?.gender === "MALE" || t.requirements?.gender === "FEMALE"
        ? t.requirements.gender
        : "",
    reqMinAge:
      t.requirements?.minAge != null ? String(t.requirements.minAge) : "",
    reqMaxAge:
      t.requirements?.maxAge != null ? String(t.requirements.maxAge) : "",
    reqMinHeightCm:
      t.requirements?.minHeightCm != null
        ? String(t.requirements.minHeightCm)
        : "",
    reqMotorbike: t.requirements?.motorbikeRequired ?? false,
    reqExcludeTattoo: t.requirements?.excludeVisibleTattoo ?? false,
    reqKyc: t.requirements?.kycCccdRequired ?? false,
    softNotes: t.softNotes ?? "",
  };
}

function formToPayload(f: FormState): AdminSystemPostingTemplateUpsertRequest {
  return {
    code: f.code.trim().toUpperCase(),
    label: f.label.trim(),
    name: f.name.trim(),
    position: f.position,
    venueType: f.venueType,
    employmentType: f.employmentType,
    title: f.title.trim(),
    description: f.description.trim(),
    salaryMin: Number(f.salaryMin) || 0,
    salaryMax: Number(f.salaryMax) || 0,
    salaryType: f.salaryType,
    defaultHeadcount: Math.max(1, Number(f.defaultHeadcount) || 1),
    benefits: f.benefits,
    skillsRequired: f.skillsRequired,
    skillsPreferred: f.skillsPreferred,
    requirements: {
      gender: (f.reqGender || null) as Gender | null,
      minAge: f.reqMinAge ? Number(f.reqMinAge) : null,
      maxAge: f.reqMaxAge ? Number(f.reqMaxAge) : null,
      minHeightCm: f.reqMinHeightCm ? Number(f.reqMinHeightCm) : null,
      motorbikeRequired: f.reqMotorbike,
      excludeVisibleTattoo: f.reqExcludeTattoo,
      kycCccdRequired: f.reqKyc,
    },
    softNotes: f.softNotes.trim() || null,
  };
}

// Form values mirror FormState (numbers kept as strings — `formToPayload`
// coerces them), so zodResolver<FormState> lines up with the upsert mapping.
const templateFormSchema = z.object({
  code: z
    .string()
    .trim()
    .min(1, "Vui lòng nhập code")
    .max(60, "Code tối đa 60 ký tự"),
  label: z
    .string()
    .trim()
    .min(1, "Vui lòng nhập label")
    .max(120, "Label tối đa 120 ký tự"),
  name: z.string().trim().max(120, "Tên nội bộ tối đa 120 ký tự"),
  position: z.custom<WorkerPosition>(),
  venueType: z.custom<VenueType>(),
  employmentType: z.custom<EmploymentType>(),
  title: z
    .string()
    .trim()
    .min(1, "Vui lòng nhập tiêu đề")
    .max(200, "Tiêu đề tối đa 200 ký tự"),
  description: z.string().trim().max(4000, "Mô tả tối đa 4000 ký tự"),
  salaryMin: z.string(),
  salaryMax: z.string(),
  salaryType: z.custom<SalaryType>(),
  defaultHeadcount: z.string(),
  benefits: z.array(z.string()),
  skillsRequired: z.array(z.string()),
  skillsPreferred: z.array(z.string()),
  reqGender: z.enum(["", "MALE", "FEMALE"]),
  reqMinAge: z.string(),
  reqMaxAge: z.string(),
  reqMinHeightCm: z.string(),
  reqMotorbike: z.boolean(),
  reqExcludeTattoo: z.boolean(),
  reqKyc: z.boolean(),
  softNotes: z.string().trim().max(2000, "Ghi chú tối đa 2000 ký tự"),
}) satisfies z.ZodType<FormState>;

// ── Skill picker ───────────────────────────────────────────────────────

function SkillPicker({
  position,
  value,
  disabled,
  onChange,
}: {
  position: WorkerPosition;
  value: string[];
  // Skills selected in the OTHER list — locked out here to prevent the same
  // skill being marked both required AND preferred.
  disabled: string[];
  onChange: (next: string[]) => void;
}) {
  const suggested = suggestSkillsForPosition(position);
  const others = ALL_SKILLS.filter((s) => !suggested.includes(s));
  const toggle = (code: string) => {
    if (disabled.includes(code)) return;
    onChange(
      value.includes(code) ? value.filter((c) => c !== code) : [...value, code],
    );
  };

  const renderChip = (code: string) => {
    const active = value.includes(code);
    const locked = disabled.includes(code);
    return (
      <button
        key={code}
        type="button"
        disabled={locked}
        onClick={() => toggle(code)}
        title={locked ? "Đã chọn ở danh sách bên kia" : undefined}
        className={
          "rounded-full border px-3 py-1 text-xs font-medium transition-colors " +
          (locked
            ? "cursor-not-allowed border-smoke bg-mist/50 text-ash line-through"
            : active
              ? "border-ink bg-ink text-paper"
              : "border-smoke bg-paper text-ink hover:bg-mist")
        }
      >
        {SKILL_LABEL_VI[code] ?? code}
      </button>
    );
  };

  return (
    <div className="space-y-3">
      {suggested.length > 0 && (
        <div>
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-ash">
            Gợi ý cho {WORKER_POSITION_LABEL_VI[position]}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {suggested.map(renderChip)}
          </div>
        </div>
      )}
      <div>
        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-ash">
          Skills khác
        </p>
        <div className="flex flex-wrap gap-1.5">{others.map(renderChip)}</div>
      </div>
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────

export function PostingTemplatesClient() {
  const list = useAdminSystemPostingTemplates();
  const create = useCreateAdminSystemPostingTemplate();
  const update = useUpdateAdminSystemPostingTemplate();
  const remove = useDeleteAdminSystemPostingTemplate();
  const { toast } = useToast();

  const [editing, setEditing] = React.useState<PostingTemplate | null>(null);
  const [creating, setCreating] = React.useState(false);
  const [pendingDelete, setPendingDelete] =
    React.useState<PostingTemplate | null>(null);

  async function handleSave(form: FormState) {
    try {
      const payload = formToPayload(form);
      if (editing) {
        await update.mutateAsync({ templateId: editing.id, body: payload });
        toast({ tone: "success", title: "Đã cập nhật mẫu" });
        setEditing(null);
      } else {
        await create.mutateAsync(payload);
        toast({ tone: "success", title: "Đã tạo mẫu" });
        setCreating(false);
      }
    } catch (err) {
      const msg =
        err && typeof err === "object" && "message" in err
          ? String((err as { message: unknown }).message)
          : "Lưu thất bại";
      toast({ tone: "error", title: msg });
    }
  }

  async function handleDelete(t: PostingTemplate) {
    try {
      await remove.mutateAsync(t.id);
      toast({ tone: "success", title: "Đã xoá mẫu" });
      setPendingDelete(null);
    } catch {
      toast({ tone: "error", title: "Xoá thất bại" });
    }
  }

  const items = list.data?.items ?? [];

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-ink">Mẫu hệ thống</h1>
          <p className="mt-1 text-sm text-ash">
            Quản lý các mẫu tin tuyển dụng dùng chung. Employer thấy các mẫu này
            ở mục “Bắt đầu nhanh” khi đăng tin.
          </p>
        </div>
        <Button
          type="button"
          onClick={() => setCreating(true)}
          className="gap-1.5"
        >
          <Plus size={16} /> Tạo mẫu mới
        </Button>
      </header>

      {list.isLoading ? (
        <Skeleton className="h-64 rounded-2xl" />
      ) : items.length === 0 ? (
        <Card className="p-8 text-center text-sm text-ash">
          Chưa có mẫu nào. Bấm “Tạo mẫu mới” để thêm.
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((t) => (
            <Card key={t.id} className="flex flex-col gap-2 p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold text-ink">
                    {t.label ?? t.name}
                  </p>
                  <p className="mt-0.5 truncate text-xs text-ash">
                    {t.code ?? "—"}
                  </p>
                </div>
                <Badge tone="mist">
                  {WORKER_POSITION_LABEL_VI[t.position]}
                </Badge>
              </div>
              <p className="text-xs text-ash">
                {VENUE_TYPE_LABEL_VI[t.venueType]} ·{" "}
                {EMPLOYMENT_TYPE_LABEL_VI[t.employmentType]}
              </p>
              <p className="line-clamp-2 text-xs text-ink/70">{t.title}</p>
              <div className="mt-auto flex justify-end gap-1.5 pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditing(t)}
                  className="gap-1"
                >
                  <Pencil size={14} /> Sửa
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setPendingDelete(t)}
                  className="gap-1 text-red-700"
                >
                  <Trash2 size={14} /> Xoá
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      {creating ? (
        <TemplateFormDialog
          mode="create"
          initial={EMPTY_FORM}
          loading={create.isPending}
          onClose={() => setCreating(false)}
          onSubmit={handleSave}
        />
      ) : null}

      {editing ? (
        <TemplateFormDialog
          mode="edit"
          initial={templateToForm(editing)}
          loading={update.isPending}
          onClose={() => setEditing(null)}
          onSubmit={handleSave}
        />
      ) : null}

      <ConfirmDialog
        open={!!pendingDelete}
        title="Xoá mẫu hệ thống?"
        description={
          pendingDelete
            ? `“${pendingDelete.label ?? pendingDelete.name}” sẽ không còn hiển thị cho employer.`
            : ""
        }
        confirmLabel="Xoá"
        destructive
        loading={remove.isPending}
        onClose={() => setPendingDelete(null)}
        onConfirm={async () => {
          if (pendingDelete) await handleDelete(pendingDelete);
        }}
      />
    </div>
  );
}

// ── Form dialog ────────────────────────────────────────────────────────

function TemplateFormDialog({
  mode,
  initial,
  loading,
  onClose,
  onSubmit,
}: {
  mode: "create" | "edit";
  initial: FormState;
  loading: boolean;
  onClose: () => void;
  onSubmit: (f: FormState) => void | Promise<void>;
}) {
  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormState>({
    resolver: zodResolver(templateFormSchema),
    mode: "onTouched",
    defaultValues: initial,
  });

  // SkillPicker suggestions follow the chosen position; watch it so the chips
  // re-bucket as the admin switches positions.
  const position = watch("position");
  const onValid = handleSubmit((values) => onSubmit(values));

  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      title={mode === "create" ? "Tạo mẫu hệ thống" : "Sửa mẫu hệ thống"}
      description="Mẫu sẽ hiển thị trong mục “Bắt đầu nhanh” khi employer đăng tin."
      footer={
        <>
          <Button type="button" variant="ghost" onClick={onClose}>
            Huỷ
          </Button>
          <Button type="button" onClick={onValid} loading={loading}>
            Lưu
          </Button>
        </>
      }
    >
      <form className="space-y-4" onSubmit={onValid} noValidate>
        <div className="grid gap-3 sm:grid-cols-2">
          <FormField label="Code (slug)" error={errors.code?.message}>
            <Controller
              control={control}
              name="code"
              render={({ field }) => (
                <Input
                  value={field.value}
                  onChange={(e) =>
                    field.onChange(
                      e.target.value.toUpperCase().replace(/\s+/g, "_"),
                    )
                  }
                  placeholder="WAITER_FT_RESTAURANT"
                  disabled={mode === "edit"}
                  invalid={!!errors.code}
                />
              )}
            />
          </FormField>
          <FormField label="Label hiển thị" error={errors.label?.message}>
            <Input
              placeholder="Phục vụ nhà hàng FT"
              invalid={!!errors.label}
              {...register("label")}
            />
          </FormField>
        </div>

        <FormField label="Tên nội bộ" error={errors.name?.message}>
          <Input
            placeholder="Để trống thì dùng label"
            invalid={!!errors.name}
            {...register("name")}
          />
        </FormField>

        <div className="grid gap-3 sm:grid-cols-3">
          <FormField label="Vị trí">
            <Controller
              control={control}
              name="position"
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  options={WORKER_POSITIONS.map((p) => ({
                    value: p,
                    label: WORKER_POSITION_LABEL_VI[p],
                  }))}
                />
              )}
            />
          </FormField>
          <FormField label="Loại quán">
            <Controller
              control={control}
              name="venueType"
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  options={VENUE_TYPES.map((v) => ({
                    value: v,
                    label: VENUE_TYPE_LABEL_VI[v],
                  }))}
                />
              )}
            />
          </FormField>
          <FormField label="Hình thức">
            <Controller
              control={control}
              name="employmentType"
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  options={EMPLOYMENT_TYPES.map((e) => ({
                    value: e,
                    label: EMPLOYMENT_TYPE_LABEL_VI[e],
                  }))}
                />
              )}
            />
          </FormField>
        </div>

        <FormField label="Tiêu đề tin" error={errors.title?.message}>
          <Input invalid={!!errors.title} {...register("title")} />
        </FormField>

        <FormField label="Mô tả" error={errors.description?.message}>
          <Textarea
            rows={4}
            invalid={!!errors.description}
            {...register("description")}
          />
        </FormField>

        <div className="grid gap-3 sm:grid-cols-4">
          <FormField label="Lương min (VND)">
            <Input type="number" {...register("salaryMin")} />
          </FormField>
          <FormField label="Lương max (VND)">
            <Input type="number" {...register("salaryMax")} />
          </FormField>
          <FormField label="Đơn vị lương">
            <Controller
              control={control}
              name="salaryType"
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  options={SALARY_TYPES.map((s) => ({
                    value: s,
                    label: SALARY_TYPE_LABEL_VI[s],
                  }))}
                />
              )}
            />
          </FormField>
          <FormField label="Số người mặc định">
            <Input type="number" min={1} {...register("defaultHeadcount")} />
          </FormField>
        </div>

        <FormField label="Phúc lợi (chọn nhiều)">
          <Controller
            control={control}
            name="benefits"
            render={({ field }) => (
              <div className="flex flex-wrap gap-1.5">
                {BENEFITS_CATALOG.map((b) => {
                  const active = field.value.includes(b);
                  return (
                    <button
                      key={b}
                      type="button"
                      onClick={() =>
                        field.onChange(
                          active
                            ? field.value.filter((x) => x !== b)
                            : [...field.value, b],
                        )
                      }
                      className={
                        "rounded-full border px-3 py-1 text-xs font-medium transition-colors " +
                        (active
                          ? "border-ink bg-ink text-paper"
                          : "border-smoke bg-paper text-ink hover:bg-mist")
                      }
                    >
                      {BENEFIT_LABEL_VI[b]}
                    </button>
                  );
                })}
              </div>
            )}
          />
        </FormField>

        <FormField
          label="Skills bắt buộc"
          description="Skill ứng viên BUỘC phải có để apply. Gợi ý theo vị trí hiển thị trước."
        >
          <Controller
            control={control}
            name="skillsRequired"
            render={({ field }) => (
              <SkillPicker
                position={position}
                value={field.value}
                disabled={watch("skillsPreferred")}
                onChange={field.onChange}
              />
            )}
          />
        </FormField>
        <FormField
          label="Skills ưu tiên"
          description="Skill có thì lợi thế nhưng không bắt buộc."
        >
          <Controller
            control={control}
            name="skillsPreferred"
            render={({ field }) => (
              <SkillPicker
                position={position}
                value={field.value}
                disabled={watch("skillsRequired")}
                onChange={field.onChange}
              />
            )}
          />
        </FormField>

        <div className="rounded-xl border border-smoke p-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ash">
            Yêu cầu cứng (tuỳ chọn)
          </p>
          <div className="grid gap-3 sm:grid-cols-4">
            <FormField label="Giới tính">
              <Controller
                control={control}
                name="reqGender"
                render={({ field }) => (
                  <Select
                    value={field.value || "ANY"}
                    onValueChange={(v) =>
                      field.onChange(
                        v === "ANY" ? "" : (v as "MALE" | "FEMALE"),
                      )
                    }
                    options={[
                      { value: "ANY", label: "Không yêu cầu" },
                      { value: "MALE", label: "Nam" },
                      { value: "FEMALE", label: "Nữ" },
                    ]}
                  />
                )}
              />
            </FormField>
            <FormField label="Tuổi min">
              <Input type="number" {...register("reqMinAge")} />
            </FormField>
            <FormField label="Tuổi max">
              <Input type="number" {...register("reqMaxAge")} />
            </FormField>
            <FormField label="Chiều cao min (cm)">
              <Input type="number" {...register("reqMinHeightCm")} />
            </FormField>
          </div>
          <div className="mt-3 flex flex-wrap gap-3 text-sm text-ink">
            <label className="flex items-center gap-2">
              <input type="checkbox" {...register("reqMotorbike")} />
              Có xe máy
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" {...register("reqExcludeTattoo")} />
              Loại trừ hình xăm lộ
            </label>
            <label className="flex items-center gap-2">
              <input type="checkbox" {...register("reqKyc")} />
              Yêu cầu KYC CCCD
            </label>
          </div>
        </div>

        <FormField label="Ghi chú mềm" error={errors.softNotes?.message}>
          <Textarea
            rows={2}
            invalid={!!errors.softNotes}
            {...register("softNotes")}
          />
        </FormField>
        <button type="submit" className="hidden" aria-hidden tabIndex={-1} />
      </form>
    </Dialog>
  );
}
